/**
 * The work record's controller: `work.model.ts` and the provenance model wired
 * to `fetchWork`, the per-entry reads (`fetchProvenance`,
 * `fetchProvenanceDocuments`, and on opening a link `fetchProvenanceAttestations`,
 * `fetchProvenanceComments`, `fetchProvenanceRequests`), and the writes —
 * `writeDraftProvenance`, `attestProvenance`, `withdrawProvenanceAttestation`,
 * `commentOnProvenance`, `requestProvenanceVerification`,
 * `withdrawProvenanceEntry`.
 *
 * It owns what the model cannot: the placed work, the chain's items with any
 * unsaved draft among them, which entry is open, the history dialog and what
 * is being composed in it, the confirmations and asks fetched per entry, the
 * busy and error pair, and the write ledger. The photographs and the agents
 * are composed from `useWorkPhotographs.ts` and `useWorkAgents.ts`, which fail
 * independently of the chain and must not take it down.
 *
 * Every write is followed by a re-read rather than a local patch. Adding a
 * link renumbers the chain, and only the server knows the new order.
 */

import type {
  ProvenanceDocumentKind,
  ProvenanceEntry,
  ProvenanceEntryKind,
  ProvenanceVerificationRequest,
  WorkEstateDisclosure,
  WorkVisibility,
  WorkAttributionControls,
  WorkCorrectionField,
  WorkVisibilityControls,
  WorkLocation,
  CollectionSummary,
} from "@ondo/shared";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  attestProvenance,
  withdrawProvenanceAttestation,
  withdrawProvenanceComment,
  commentOnProvenance,
  fetchCollections,
  fetchWork,
  fetchProvenance,
  fetchProvenanceAttestations,
  fetchProvenanceComments,
  fetchProvenanceDocuments,
  fetchProvenanceRequests,
  judgeProvenanceDocument,
  correctWork,
  moveWork,
  removeWorkFromCollection,
  requestProvenanceVerification,
  setEstateIdentityHidden,
  setEstateRecordAccess,
  setWorkAttributionPublic,
  setWorkLocationPublic,
  setWorkVisibility,
  withdrawProvenanceDocumentVerdict,
  withdrawProvenanceEntry,
} from "../lib/api.ts";
import { useAccount } from "../lib/account.ts";
import { entityNamesOf } from "../lib/works.ts";
import { newLedger, writeDraftProvenance } from "../lib/provenanceWrite.ts";
import type { ProvenanceHandlers } from "../provenance/ProvenanceEntryList.tsx";
import { PROVENANCE_COPY } from "../provenance/provenance.constants.ts";
import { PROVENANCE_SURFACES } from "../provenance/provenance.surfaces.ts";
import { useProvenanceSpotlight } from "../provenance/useProvenanceSpotlight.ts";
import type { SpotlightView } from "../spotlight/useSpotlight.ts";
import {
  addDocuments,
  addEntry,
  askBody,
  attachOndoId,
  detachOndoId,
  isDraft,
  readParty,
  recordAct,
  removeAct,
  removeDocument,
  removeEntry,
  setDocumentKind,
  setEntryField,
  setEntryKind,
  toRecordedItems,
  validateEntry,
  verdictRefusal,
} from "../provenance/provenance.model.ts";
import type {
  ConfirmationLoad,
  DraftEntry,
  ProvenanceItem,
  VerificationIntent,
} from "../provenance/provenance.model.ts";
import { useWorkAgents } from "./useWorkAgents.ts";
import { useWorkRepresentation } from "./useWorkRepresentation.ts";
import { useWorkDisclosure } from "./useWorkDisclosure.ts";
import type { DisclosureHandlers, RuleDraft } from "./useWorkDisclosure.ts";
import type { DisclosurePanel } from "./disclosure.model.ts";
import type { AgentHandlers } from "./useWorkAgents.ts";
import { useWorkPhotographs } from "./useWorkPhotographs.ts";
import { DEFAULT_WORK_TAB } from "./work.constants.ts";
import type { WorkTabId } from "./work.constants.ts";
import {
  claimedArtistOf,
  describeError,
  panelShows,
  resolveWorkRecordView,
  toPanel,
} from "./work.model.ts";
import type { PlacedWork, WorkRecordView } from "./work.model.ts";

// Re-exported so the screen and the assign dialog keep one import for the
// controller's whole vocabulary; the shapes live where they are built.
export type { AgentHandlers, WorkRecordView };

export interface WorkController {
  view: WorkRecordView;
  provenance: ProvenanceHandlers;
  /** The document viewer, mounted once by the screen. */
  spotlight: SpotlightView;
  closeDocument: () => void;
  /** Opens the modal. Picks up an unsaved draft rather than starting over. */
  addProvenanceEntry: () => void;
  /** Opens the modal with the Artist attribution chip already pressed. */
  askAttribution: () => void;
  /**
   * Lands the reader on one entry: the tab that holds every kind, expanded.
   *
   * The attribution panel's one link, and it goes to the claim's own row
   * because that is where the request door lives. `?entry=` already does this
   * from a notification; this is the same arrival without a round trip through
   * the URL, since the reader is already on the record.
   */
  openEntry: (entryId: string) => void;
  history: HistoryHandlers;
  /** Opens the assign modal, keeping whatever was typed before. */
  assignAgent: () => void;
  agents: AgentHandlers;
  /** What the artist's estate may see, and the two switches behind it — OND-58. */
  estate: EstateHandlers;
  /** How far the work travels, and the one radio group behind it — OND-47. */
  visibility: VisibilityHandlers;
  /** Which collection holds the work, and the two acts that change it — OND-22. */
  placement: PlacementHandlers;
  /** Correct one catalogue field — OND-41. */
  correction: CorrectionHandlers;
  /** What the artist's estate publishes about its own artist — OND-172. */
  attributionVisibility: AttributionVisibilityHandlers;
  disclosure: DisclosureCluster;
  /**
   * The location panel saved a row: the record's own state says so too.
   *
   * Patched into `placed` rather than re-read, because the response IS the row
   * and a second request to learn what we were just told would be the
   * shape `useWorkAgents` calls "two spinners for one act". Nothing else on
   * the record depends on the location, so nothing else moves.
   */
  applyLocation: (location: WorkLocation) => void;
  /**
   * Read the whole record again — OND-173.
   *
   * THE OPPOSITE CHOICE FROM `applyLocation` ABOVE, and for the opposite
   * reason. That one patches because the response IS the row. A sale is the
   * other extreme: `ondo.effect_owner_sale()` writes a transfer, a line of
   * provenance and, on an off-ONDO buyer, settlement rows, and then hands the
   * rest to `saleConsequences` on the engine — so nothing local could guess
   * which of them has landed, and a screen answering "what is true" from its
   * own optimistic copy would be the feed-as-truth bug in a costlier register.
   *
   * `reloadKey` has existed since the correction panel needed it and was
   * private to this hook; this exposes it, so a panel whose act changes the
   * whole record can ask for one read rather than knowing what to patch.
   */
  reload: () => void;
  selectImage: (imageId: string) => void;
  selectTab: (tab: WorkTabId) => void;
}

/**
 * The estate panel's two switches and its own progress pair — OND-58.
 *
 * TWO HANDLERS, named for the control each one moves. One handler taking which
 * control would be the place "naming the owner is not a grant of record access"
 * stopped being structural and became a convention.
 *
 * `busy` and `failed` are this panel's own and not the record's: the provenance
 * write's pair renders inside the provenance panel, and a visibility switch
 * reporting its failure there would describe one act in another act's words.
 */
export interface EstateHandlers {
  busy: boolean;
  failed: boolean;
  onSetHidden: (hidden: boolean) => void;
  onSetRecordOpen: (open: boolean) => void;
}

/**
 * The visibility panel's own bundle — OND-47.
 *
 * ONE HANDLER TAKING THE RUNG, which is the opposite of the pair above and the
 * difference is what is being said. The estate's two are independent controls,
 * so a single handler taking a mode would have lost which one the owner moved;
 * these three are rungs on one scale, so a handler per rung would be three
 * ways of writing one field with nothing recording that they are one question.
 *
 * AND ONE HANDLER BESIDE IT, taking a boolean — OND-172's location opt-in. It is
 * on this bundle because it is on this panel and is the same party's, and it is a
 * SECOND handler rather than a second argument to the first because the ticket
 * requires that publishing a work can never publish where it is kept. Two
 * handlers is the shape in which that cannot be got wrong by a screen.
 *
 * `busy` and `failed` are this panel's own, as the estate pair's are: a radio
 * group greyed out because an estate switch was saving would be describing one
 * act in another act's words. The two controls here DO share them, because they
 * are two acts on one panel by one party, and a reader cannot press both at once.
 */
export interface VisibilityHandlers {
  busy: boolean;
  failed: boolean;
  onChoose: (visibility: WorkVisibility) => void;
  /** Whether a public view says where the work is kept. Off by default, always. */
  onChooseLocationPublic: (locationPublic: boolean) => void;
}

/**
 * The placement panel's bundle — OND-22.
 *
 * TWO ACTS AND A SAFETY CATCH. Moving takes the destination; removing is asked
 * for twice, because it is the one act on this record after which the reader
 * may not be able to open it again. `onArmRemove` turns the button into a
 * question and `onCancelRemove` takes it back; `onRemove` is only ever offered
 * while armed. That is state about the SCREEN and so it lives here rather than
 * in the component, where a second click could not be asserted without React.
 *
 * `failed` carries the server's sentence rather than a boolean, unlike the
 * visibility pair, because one refusal here is actionable: "this work has been
 * sold and is awaiting its buyer" tells the owner exactly why the move stopped.
 */
export interface PlacementHandlers {
  busy: boolean;
  failed: string | null;
  onMove: (collectionId: string) => void;
  onArmRemove: () => void;
  onCancelRemove: () => void;
  onRemove: () => void;
}

/**
 * The correction panel's bundle — OND-41. Choosing a field and typing a value
 * are screen state and live here; whether the panel exists at all is the
 * model's (`toCorrectionPanel`), and what a correction IS is the database's.
 */
export interface CorrectionHandlers {
  busy: boolean;
  failed: string | null;
  onChooseField: (field: WorkCorrectionField) => void;
  onChangeValue: (value: string) => void;
  onSubmit: () => void;
}

/**
 * The ESTATE's bundle — OND-172, and the only one on this record that is not the
 * owner's.
 *
 * ONE HANDLER AND ITS OWN PROGRESS PAIR. Its own pair rather than a share of the
 * visibility panel's, for a reason stronger than the one the other panels give:
 * no caller ever sees both panels, because the server answers one of them null
 * for each party. State shared between two screens that never coexist is state
 * that cannot be reasoned about.
 */
export interface AttributionVisibilityHandlers {
  busy: boolean;
  failed: boolean;
  onChoose: (attributionPublic: boolean) => void;
}

/**
 * The disclosure panel's own bundle — OND-79.
 *
 * A CLUSTER AND NOT A HANDLER BAG, which is the third shape on this record and
 * the difference is again what is being said. The estate has two independent
 * switches; the ceiling has one ordered scale; this has an unbounded SET of
 * rules at two grains, so the panel needs the rules themselves, the draft of
 * the one being written, and the entries a rule can name.
 *
 * `panel` CARRIES ITS OWN null, and it means something narrower than "no
 * rules": `GET /disclosure` refuses a caller who is neither the owner nor an
 * agent on the work, so null is "not yours to see" and an empty `rows` is "you
 * may manage these and there are none". A screen that conflated them would
 * either show a stranger an empty control or show an owner nothing.
 */
export interface DisclosureCluster {
  panel: DisclosurePanel | null;
  draft: RuleDraft;
  /** The recorded entries a rule may name, from the chain already loaded. */
  entries: ProvenanceEntry[];
  busy: boolean;
  failed: boolean;
  handlers: DisclosureHandlers;
}

/** What the modal does, as the one bundle the screen hands it. */
export interface HistoryHandlers {
  onChooseKind: (kind: ProvenanceEntryKind) => void;
  onSetField: <K extends keyof DraftEntry>(key: K, value: DraftEntry[K]) => void;
  onAddDocuments: (files: File[]) => void;
  onSetDocumentKind: (documentId: string, kind: ProvenanceDocumentKind) => void;
  onOpenDocument: (documentId: string) => void;
  onRemoveDocument: (documentId: string) => void;
  onAttachOndoId: (raw: string) => string | null;
  onDetachOndoId: () => void;
  onAct: (intent: VerificationIntent) => string | null;
  onUnact: (actId: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onClose: () => void;
}

/**
 * One work, and its chain of title.
 *
 * Provenance here is the same components the register wizard uses, on the
 * `record` surface: the rows exist on the server, so the verification actions
 * are live — for whoever the database says may take them. `canAttest` is
 * computed from a live or answered request, so a button this offers is one the
 * RPC will accept and one it withholds is one that would have been refused.
 */
export function useWorkController(input: {
  workId: string;
  /**
   * Which of the reader's own firms this read is recorded against — OND-68.
   *
   * A PROP RATHER THAN SOMETHING THIS HOOK WORKS OUT, because only the
   * workspace knows which seat is open: the same person may hold one at a
   * gallery and read as themselves five minutes later, and the trail has to say
   * which. Null is a real value and not a missing one — a private collector has
   * no firm, and their reads are their own.
   *
   * IT CANNOT WIDEN OR NARROW THE RECORD. `ondo.work_record()` never sees it;
   * `works_read` decides the read exactly as before. The only thing it changes
   * is whose trail the access row lands in, which is why passing it wrongly
   * loses a line from a firm's audit rather than disclosing anything.
   */
  actingAs: string | null;
  /**
   * A link in the chain to open on arrival, from `?entry=` on the URL.
   *
   * Seeds `openEntryId` rather than replacing it: once the reader has expanded
   * or collapsed anything, the URL has had its say and the screen is theirs. A
   * notification whose answer is on one link lands the reader looking at that
   * link, and behaves like an ordinary record from then on.
   */
  focusEntryId: string | null;
  /**
   * Which tab the URL names.
   *
   * A PROP AND NOT STATE, and that is the whole point. There is no local copy to
   * keep in step, so Back, a pasted link and a hand-edited address bar all mean
   * the same thing and no effect has to correct anything. The `?entry=` seeding
   * two fields up is what a second copy costs, and one of those is enough.
   */
  tab: WorkTabId;
  /** Writes the tab to the URL. The only way this screen changes tab. */
  onSelectTab: (tab: WorkTabId) => void;
  /**
   * `capabilities.assignAgent`, threaded from the workspace.
   *
   * A PROP AND NOT A LOCAL TEST, and never `if (featureNotBuilt)`. The server
   * computes it from `exists(owned_work_ids())`, so the Assign control appears
   * exactly when the database would accept the write — see `Capabilities`.
   */
  canAssignAgent: boolean;
  /**
   * Whether this SEAT may write the work's history — `WorkRecord`'s own prop,
   * passed through rather than re-derived.
   *
   * Here because the attribution panel's ask is a history write, and whether to
   * offer it is a rule rather than a rendering choice. The alternative was the
   * view combining a prop with a model answer, which is the shape the frontend
   * conventions exist to refuse. Defaults true, which is the personal-workspace
   * answer: a collector on their own work has no rung and ownership decides.
   */
  canWriteHistory?: boolean;
  /**
   * What the screen does once the work has left its collection — OND-22.
   *
   * A CALLBACK AND NOT A NAVIGATION, because this hook does not know where the
   * reader came from and must not guess: the record is reached from five
   * workspaces with five "back" addresses, and `WorkRecord` already holds the
   * right one as `collectionHref`. Called after the server has confirmed the
   * removal and never before, so a refused removal leaves the reader exactly
   * where they were, looking at the refusal.
   */
  onRemoved?: () => void;
}): WorkController {
  const { workId, actingAs, focusEntryId, tab } = input;
  // OND-52. The entities the reader acts for, so the holder line can NAME the
  // one that holds this work rather than saying "Yours" of a company's picture.
  // Read from the one copy of the account — no fetch, and no id in it the
  // reader could not already name.
  const { me: meState } = useAccount();
  const entityNames = entityNamesOf(meState.status === "ready" ? meState.me : null);
  const [placed, setPlaced] = useState<PlacedWork | null>(null);
  // OND-22. The owner's other collections, asked for once the record says the
  // reader owns the holding one; null until then and for everybody else.
  const [collections, setCollections] = useState<CollectionSummary[] | null>(null);
  const [placementBusy, setPlacementBusy] = useState(false);
  const [placementFailed, setPlacementFailed] = useState<string | null>(null);
  const [removeArmed, setRemoveArmed] = useState(false);
  // OND-41. The correction draft: which field and what it should say. Reset to
  // the year with nothing typed after a correction lands, so the next one starts
  // clean; the record is then read again, which is how the mark appears.
  const [correctionField, setCorrectionField] = useState<WorkCorrectionField>("year");
  const [correctionValue, setCorrectionValue] = useState("");
  const [correctionBusy, setCorrectionBusy] = useState(false);
  const [correctionFailed, setCorrectionFailed] = useState<string | null>(null);
  // Bumped after a move so the record is read again under its new placement
  // rather than patched locally: `work_record()` answers more than the
  // collection's name — the placement rows, the location's disclosure — and a
  // screen that rewrote one field would be guessing at the rest.
  const [reloadKey, setReloadKey] = useState(0);
  const [items, setItems] = useState<ProvenanceItem[]>([]);
  const [openEntryId, setOpenEntryId] = useState<string | null>(focusEntryId);
  const [refusals, setRefusals] = useState<
    { entryId: string; sentences: string[] } | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // OND-58. The estate panel's own pair, deliberately NOT the `busy`/`error`
  // above: that pair belongs to the provenance write and renders inside the
  // provenance panel, and a visibility switch that greyed out the history form
  // — or printed its failure there — would be reporting one act in another
  // act's words.
  const [estateBusy, setEstateBusy] = useState(false);
  const [estateFailed, setEstateFailed] = useState(false);
  // OND-47. Its OWN pair, for the reason the estate pair is its own: this
  // panel is a different act, and a radio group greyed out by the estate
  // switch saving would be reporting one act in another act's words.
  const [reachBusy, setReachBusy] = useState(false);
  const [reachFailed, setReachFailed] = useState(false);
  // OND-172. The ESTATE's own pair, and a third pair rather than a share of the
  // owner's for a reason the two above only hint at: this is a different
  // PARTY's control, not merely a different act. Nobody ever sees both panels,
  // because the server answers one of them null for each caller — so sharing a
  // `busy` flag would be sharing state between two screens that never coexist.
  const [attributionBusy, setAttributionBusy] = useState(false);
  const [attributionFailed, setAttributionFailed] = useState(false);
  /**
   * The modal, and the draft it is composing.
   *
   * Two pieces of state and not one, because closing is not discarding: the
   * draft outlives the dialog being shut, so Escape puts it down and
   * "+ Add history" picks it back up. Only Cancel clears `composing`.
   */
  const [dialogOpen, setDialogOpen] = useState(false);
  const [composing, setComposing] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmations, setConfirmations] = useState<Record<string, ConfirmationLoad>>({});
  /** Outstanding asks by entry id, fetched beside the confirmations. */
  const [asks, setAsks] = useState<Record<string, ProvenanceVerificationRequest[]>>({});

  /**
   * A link opened while the screen is already showing this work.
   *
   * `useState` seeds once and would ignore a second notification about the same
   * record. Guarded on the id so it does not fight the reader's own toggling —
   * this fires when the URL changes, not when they collapse what it opened.
   */
  useEffect(() => {
    if (focusEntryId) setOpenEntryId(focusEntryId);
  }, [focusEntryId]);

  /** The same guard the wizard uses, and for the same reason. See the writer. */
  const ledger = useRef(newLedger());

  const { view: spotlight, openDocument, closeDocument } = useProvenanceSpotlight(
    items,
    PROVENANCE_SURFACES.record,
  );

  /**
   * Reads the chain from the server.
   *
   * Returns rather than sets, so the effect below writes state in a callback
   * exactly as the collections effect does — and so `run` can await it after a
   * write. Always a re-read rather than a local patch: adding a link renumbers
   * the ones after it, and an attestation changes a count this client does not
   * compute. The server is the only thing that knows what the chain now says.
   */
  const loadProvenance = useCallback(async (): Promise<ProvenanceItem[]> => {
    const entries = await fetchProvenance(workId);
    const documents = await Promise.all(
      entries
        .filter((entry) => entry.documentCount > 0)
        .map(async (entry) => [entry.id, await fetchProvenanceDocuments(entry.id)] as const),
    );

    return toRecordedItems(entries, Object.fromEntries(documents));
  }, [workId]);

  /**
   * The work, in ONE request, asked for by the id the URL names.
   *
   * This replaced a fan-out — `fetchCollections()` then one
   * `fetchCollectionWorks` per collection — which was the collection door and
   * only the collection door. An agent holds no collection grant, so it
   * returned an empty list and every "Open record" link in the agent workspace
   * rendered "not found" on a work the database was willing to show them.
   *
   * `collection` comes back null for that caller, and the model says so on
   * screen rather than leaving a blank. Not-found and not-permitted are one
   * 404, decided in SQL: `describeError` renders the server's own sentence and
   * this hook draws no distinction of its own.
   */
  useEffect(() => {
    let active = true;

    // OND-68. `actingAs` says which of the reader's own firms this read is
    // recorded against; it cannot change what comes back, because
    // `ondo.work_record()` never receives it. In the effect's dependency list
    // for the ordinary reason — a reader who switches workspace is reading as
    // somebody else, and the trail should say so.
    fetchWork(workId, actingAs)
      .then((record) => {
        if (active) setPlaced(record);
      })
      .catch((cause: unknown) => {
        if (active) setLoadError(describeError(cause));
      });

    return () => {
      active = false;
    };
    // `reloadKey` is in the list so a move reads the record again under its new
    // placement (OND-22); it is not read inside, which is the point of a key.
  }, [workId, actingAs, reloadKey]);

  /**
   * OND-22. The destinations for a move, asked for only when the record says
   * the reader owns the holding collection — the one caller the panel exists
   * for. Everybody else never makes this request, so an agent opening a client's
   * work does not fetch a list of collections they could not be shown.
   */
  const ownsPlacement = placed?.collection?.level === "owner";
  useEffect(() => {
    if (!ownsPlacement) return;
    let active = true;

    fetchCollections()
      .then((list) => {
        if (active) setCollections(list);
      })
      .catch(() => {
        // The panel shows "no other collection" rather than a failure: the move
        // control is a convenience and the record is already on screen. A reader
        // who needed the list can reload; nothing was written.
        if (active) setCollections([]);
      });

    return () => {
      active = false;
    };
  }, [ownsPlacement, reloadKey]);

  const movePlacement = useCallback(
    async (collectionId: string): Promise<void> => {
      setPlacementBusy(true);
      setPlacementFailed(null);
      try {
        await moveWork(workId, collectionId);
        // Read it back rather than patch it: see `reloadKey`.
        setReloadKey((key) => key + 1);
      } catch (cause) {
        // THE SENTENCE IS KEPT, unlike the visibility panel's refusal, because
        // one of the four refusals here tells the owner something they can act
        // on: a work sold and awaiting its buyer cannot be filed anywhere until
        // title lands (OND-17), and the database says exactly that.
        setPlacementFailed(describeError(cause));
      } finally {
        setPlacementBusy(false);
      }
    },
    [workId],
  );

  const submitCorrection = useCallback(async (): Promise<void> => {
    setCorrectionBusy(true);
    setCorrectionFailed(null);
    try {
      await correctWork(workId, correctionField, correctionValue.trim() || null);
      setCorrectionValue("");
      // Read it back rather than patch it: the mark on the identity block comes
      // from `corrections` on the record, and the value from `work`, and a
      // screen that rewrote one would be the "not just true" the ticket forbids.
      setReloadKey((key) => key + 1);
    } catch (cause) {
      // THE SENTENCE IS KEPT: "that is already what the record says" and "you
      // cannot correct this work" are both the database telling the reader
      // something they can act on.
      setCorrectionFailed(describeError(cause));
    } finally {
      setCorrectionBusy(false);
    }
  }, [workId, correctionField, correctionValue]);

  const removePlacement = useCallback(async (): Promise<void> => {
    setPlacementBusy(true);
    setPlacementFailed(null);
    try {
      await removeWorkFromCollection(workId);
      // After the server has said so and never before: a refused removal must
      // leave the reader on the record, looking at the refusal.
      input.onRemoved?.();
    } catch (cause) {
      setRemoveArmed(false);
      setPlacementFailed(describeError(cause));
    } finally {
      setPlacementBusy(false);
    }
    // `input.onRemoved` is read at call time on purpose; a stale closure here
    // would navigate to wherever the reader WAS when the record loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workId]);

  const { photographs, selectedImageId, selectImage } = useWorkPhotographs(workId);

  useEffect(() => {
    let active = true;

    loadProvenance()
      .then((loaded) => {
        if (active) setItems(loaded);
      })
      .catch((cause: unknown) => {
        if (active) setLoadError(describeError(cause));
      });

    return () => {
      active = false;
    };
  }, [loadProvenance]);

  /**
   * The owner's two estate controls — OND-58.
   *
   * TWO HANDLERS, one per control, matching the two routes and the two SQL
   * doors. A single handler taking which-switch would be the first place the
   * two distinct controls became one setting.
   *
   * THE SERVER'S ANSWER IS ADOPTED, never the value that was sent. Both writes
   * return the effective state of BOTH controls, so this cannot drift from the
   * database about the switch it did not touch — and on a refusal nothing is
   * adopted at all, which is why the panel can truthfully say the settings are
   * unchanged.
   */
  const applyDisclosure = useCallback((next: WorkEstateDisclosure): void => {
    setPlaced((current) =>
      current === null ? current : { ...current, estateDisclosure: next },
    );
  }, []);

  const setEstateHidden = useCallback(
    async (hidden: boolean): Promise<void> => {
      setEstateBusy(true);
      setEstateFailed(false);
      try {
        applyDisclosure(await setEstateIdentityHidden(workId, hidden));
      } catch {
        // No message is parsed out of the refusal, and that is deliberate: the
        // only refusal this door gives is "you do not own that work", which a
        // reader looking at their own control panel cannot act on. The panel
        // says the save failed and the settings stand.
        setEstateFailed(true);
      } finally {
        setEstateBusy(false);
      }
    },
    [applyDisclosure, workId],
  );

  const setEstateRecordShared = useCallback(
    async (open: boolean): Promise<void> => {
      setEstateBusy(true);
      setEstateFailed(false);
      try {
        applyDisclosure(await setEstateRecordAccess(workId, open));
      } catch {
        setEstateFailed(true);
      } finally {
        setEstateBusy(false);
      }
    },
    [applyDisclosure, workId],
  );

  /**
   * OND-47. The server's answer is adopted, never the value that was sent.
   *
   * `ondo.set_work_visibility()` answers in the same shape the panel reads, so
   * `hasRegion` comes back with the rung — which matters on exactly the move
   * this panel exists to make easy: an owner selecting "Region only" on a work
   * with no country recorded is told so by the answer to their own click,
   * rather than by nothing happening.
   */
  const applyVisibility = useCallback((next: WorkVisibilityControls): void => {
    setPlaced((current) =>
      current === null ? current : { ...current, visibility: next },
    );
  }, []);

  const chooseVisibility = useCallback(
    async (visibility: WorkVisibility): Promise<void> => {
      setReachBusy(true);
      setReachFailed(false);
      try {
        applyVisibility(await setWorkVisibility(workId, visibility));
      } catch {
        // No message is parsed out, as on the estate doors and for the same
        // reason: the only refusal is "you do not own that work", which a
        // reader looking at their own control panel cannot act on. Nothing is
        // adopted, so the panel can truthfully say the setting is unchanged.
        setReachFailed(true);
      } finally {
        setReachBusy(false);
      }
    },
    [applyVisibility, workId],
  );

  /**
   * OND-172. The location opt-in — A SECOND HANDLER, NOT A SECOND ARGUMENT.
   *
   * The ticket asks that turning a work public never turn its location public,
   * and the way that is kept here is that the two acts cannot be expressed as
   * one call at any layer: two SQL doors, two routes, two client functions, two
   * handlers. It shares the reach panel's busy pair because it is on the same
   * panel and the same party's, which the estate control below is not.
   */
  const chooseLocationPublic = useCallback(
    async (locationPublic: boolean): Promise<void> => {
      setReachBusy(true);
      setReachFailed(false);
      try {
        applyVisibility(await setWorkLocationPublic(workId, locationPublic));
      } catch {
        setReachFailed(true);
      } finally {
        setReachBusy(false);
      }
    },
    [applyVisibility, workId],
  );

  /**
   * OND-172. The estate's half, and the server's answer is adopted as ever.
   *
   * `ondo.set_work_attribution_public()` answers in the panel's own shape, which
   * carries `contested` — derived from the work's history rather than from this
   * control — so an estate turning the name back on sees in one answer whether
   * the public view will mark the attribution as disputed.
   */
  const applyAttributionControls = useCallback(
    (next: WorkAttributionControls): void => {
      setPlaced((current) =>
        current === null ? current : { ...current, attributionControls: next },
      );
    },
    [],
  );

  const chooseAttributionPublic = useCallback(
    async (attributionPublic: boolean): Promise<void> => {
      setAttributionBusy(true);
      setAttributionFailed(false);
      try {
        applyAttributionControls(
          await setWorkAttributionPublic(workId, attributionPublic),
        );
      } catch {
        setAttributionFailed(true);
      } finally {
        setAttributionBusy(false);
      }
    },
    [applyAttributionControls, workId],
  );

  const loadConfirmations = useCallback(async (entryId: string): Promise<void> => {
    setConfirmations((current) => ({ ...current, [entryId]: { kind: "loading" } }));

    try {
      // Three in parallel rather than two. The asks are the one part of a
      // verification the asker could not see anywhere: a request queued in the
      // wizard is sent at submit and then vanished, because a recorded entry
      // carries a count of open requests and nothing rendered it.
      const [attestations, comments, requests] = await Promise.all([
        fetchProvenanceAttestations(entryId),
        fetchProvenanceComments(entryId),
        // Refused for anyone who did not ask, which is most readers. Their
        // `openRequestCount` is 0, so the panel asks for nothing anyway; an
        // empty list on a refusal keeps a 403 from taking the card down.
        fetchProvenanceRequests(entryId).catch((): ProvenanceVerificationRequest[] => []),
      ]);
      setAsks((current) => ({ ...current, [entryId]: requests }));
      setConfirmations((current) => ({
        ...current,
        [entryId]: { kind: "loaded", attestations, comments },
      }));
    } catch (cause: unknown) {
      setConfirmations((current) => ({
        ...current,
        [entryId]: { kind: "failed", message: describeError(cause) },
      }));
    }
  }, []);

  /**
   * Who has put their name to one link, fetched when that link is opened.
   *
   * Lazy on purpose. The counts are already on the entry, so the closed card
   * can say "2 confirmations" without asking anybody — pulling every
   * attestation and comment for a forty-link chain to render lines nobody has
   * opened would be eighty requests for nothing.
   *
   * A failure here is confined to the card: the strip above it still shows the
   * counts the chain returned, and the message says the record has not changed.
   *
   * WHICHEVER WAY THE CARD CAME OPEN, and that is why this is an effect on
   * `openEntryId` rather than a line in the handler that opened it. There are
   * THREE ways: the reader presses the row, a panel calls `openEntry`, and the
   * URL arrives carrying `?entry=` — and the third one never asked. The first
   * two each had their own copy of this condition and the deep link had none,
   * so a reader following a link straight to one entry was shown an OPEN card
   * with no confirmations, no disputes and no reason on it.
   *
   * IT IS A LINK THE PRODUCT SENDS PEOPLE DOWN. The insurance panel's own
   * banner says "Open it in the artwork history to read the reason and who
   * recorded it" and its "See in artwork history" button IS this deep link, so
   * the one route offered for reading a dispute's reason led to the one card
   * that had not asked for it — found by `zzz-insurance.spec.ts`, which is the
   * only layer that could.
   *
   * It waits for `items`, because `isDraft` cannot answer before the chain is
   * in; and it asks once per entry, because `confirmations` holds the key from
   * the first ask onwards in all three of its states. The explicit re-reads
   * after a write stay explicit — those exist to replace a list that is
   * already loaded.
   */
  useEffect(() => {
    if (openEntryId === null || items.length === 0) return;
    if (isDraft(items, openEntryId) || openEntryId in confirmations) return;

    void loadConfirmations(openEntryId);
  }, [openEntryId, items, confirmations, loadConfirmations]);

  /**
   * One write, then a re-read.
   *
   * `drop` is the local draft the write turned into a row. Everything else that
   * is still local survives the reload — those entries are not on the server, so
   * the re-read cannot know about them, and dropping them would throw away
   * typing the collector has not finished.
   */
  const run = async (action: () => Promise<void>, drop?: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      const loaded = await loadProvenance();
      setItems((current) => [
        ...loaded,
        ...current.filter((item) => item.kind === "draft" && item.entry.id !== drop),
      ]);
    } catch (cause: unknown) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  /** The draft the modal is composing, or null. */
  const composingItem = items.find(
    (item) => item.kind === "draft" && item.entry.id === composing,
  );
  const draft = composingItem?.kind === "draft" ? composingItem.entry : null;

  /**
   * Writes the composed entry to the record, with everything attached to it.
   *
   * The one-way door. After this the row is the registry's and can only be
   * withdrawn, which is why the modal says so above the button. The ledger
   * survives a failed save, so pressing it again after a stalled upload does not
   * file the entry, the verification or the asks a second time.
   *
   * Validated here as well as by the API and by the check constraint, and the
   * reason is the door: a 400 arrives after the collector has pressed a button
   * that says this cannot be undone, and it is better to name the empty field
   * before they press it.
   */
  const submitHistory = () => {
    if (!composingItem || composingItem.kind !== "draft" || composing === null) return;

    const problem = validateEntry(composingItem.entry);
    if (problem !== null) {
      setError(problem);
      return;
    }

    void run(async () => {
      const written = await writeDraftProvenance([composingItem], workId, ledger.current);
      // `run` clears the error before the action, so a refusal set here is what
      // the next render shows. The entry itself is on the record by now —
      // refusals are the documents and the asks that did not follow it — so the
      // modal closes either way and the sentence is shown on the record behind.
      if (written.refusals.length > 0) setError(written.refusals.join(" "));
      setComposing(null);
      setDialogOpen(false);
      setOpenEntryId(null);
      // The chips are the whole vocabulary on every tab — the modal is not
      // scoped to where it was opened from — so an exhibition can be recorded
      // while standing on Condition. Filing it out of sight is the one outcome
      // this must not have: the reader has just pressed a button that says this
      // cannot be undone, and the screen would be unchanged. Artwork history
      // holds every kind, so it is always somewhere the new entry can be seen.
      if (!panelShows(toPanel(tab), composingItem.entry.kind)) {
        input.onSelectTab(DEFAULT_WORK_TAB);
      }
    }, composing);
  };

  /**
   * A draft records the act; a row on the registry is written now.
   *
   * Which one it is is a question about the shape of the list rather than a
   * rule, so the model answers it. The composer supplies whatever the act needs
   * typed, which is why this takes an intent rather than a key and a prompt.
   */
  const act = (entryId: string, intent: VerificationIntent): string | null => {
    if (isDraft(items, entryId)) {
      const recorded = recordAct(items, entryId, intent);
      if (!recorded.problem) setItems(recorded.items);
      return recorded.problem;
    }

    const write = (): Promise<void> => {
      if (intent.key === "request") {
        // Re-read here rather than trusting the typed string, so the immediate
        // path applies the same ONDO-id-or-address rule the draft path does.
        // The model refuses what it cannot classify; this cannot, so it asks.
        const party = readParty(intent.party);
        if (!party) return Promise.reject(new Error(PROVENANCE_COPY.askInvalid));
        return requestProvenanceVerification(entryId, askBody(party));
      }
      if (intent.key === "comment") {
        return commentOnProvenance(entryId, { body: intent.body });
      }
      // The body is required on a dispute, not optional: an accusation with no
      // content is one the other side cannot answer, and the check constraint
      // refuses it anyway.
      if (intent.key === "dispute") {
        return attestProvenance(entryId, { stance: "dispute", body: intent.body });
      }
      return attestProvenance(entryId, { stance: "verify" });
    };

    // Re-read the card's own list as well as the chain. The counts come back on
    // the entry, but the name and the words that were just added only exist in
    // the attestations and comments this card is showing — a strip that ticked
    // up to 3 above a list still showing 2 would read as a lost write.
    void run(write).then(() => loadConfirmations(entryId));

    return null;
  };

  /**
   * Taking a live position back.
   *
   * `POST /v1/provenance-attestations/:id/withdraw` has existed since
   * 20260804090500 and had no caller in the app at all — which is why
   * `PROVENANCE_COPY.positionHeld` told people to "take the one you have back
   * first" beside no control that could.
   *
   * ONE LIVE POSITION EACH is the rule this closes the loop on. Verify and
   * Dispute both shut the moment either is held, so an account that confirmed
   * an entry and then found reason to dispute it had no route between the two
   * except asking somebody with database access.
   *
   * The row is NOT deleted — `ended_at` is set and the entry stays on the list
   * marked withdrawn, because the position was held and a count that had been
   * seen must not silently disagree with itself. Refreshes the same two things
   * an attestation does, and for the same reason: the counts live on the chain
   * and the names live on the card.
   */
  const withdrawAttestation = (entryId: string, attestationId: string): void => {
    void run(() => withdrawProvenanceAttestation(attestationId)).then(() =>
      loadConfirmations(entryId),
    );
  };

  /**
   * Taking a comment back — OND-21.
   *
   * The same shape as `withdrawAttestation`, and deliberately not folded into
   * it: a comment moves no count on the chain, so the re-read of the chain
   * `run` performs is for the error path and the card's names, not for a number
   * that changed. The confirmations panel is reloaded so the row shows its mark
   * from the record rather than from a flag set here.
   */
  const withdrawComment = (entryId: string, commentId: string): void => {
    void run(() => withdrawProvenanceComment(commentId)).then(() =>
      loadConfirmations(entryId),
    );
  };

  const workAgents = useWorkAgents({ workId, setBusy, setError });
  // OND-73. The viewer's reading of the same question; the model hides it for
  // whoever manages the agents, so the two panels never both draw.
  const representation = useWorkRepresentation(workId);

  /**
   * OND-79. Who sees which line of this work's history.
   *
   * IT IS HANDED THE RECORDED ENTRIES THE RECORD HAS ALREADY LOADED, rather
   * than fetching the chain a second time: a rule names an entry by id and the
   * owner needs to read which line that is. One source, so the panel and the
   * history cannot disagree about what an entry says — and an entry the caller
   * cannot read is simply absent from `items`, which the model renders as the
   * withheld sentence rather than as a blank.
   */
  const disclosureEntries = items.flatMap((item) =>
    item.kind === "recorded" ? [item.entry] : [],
  );
  const workDisclosure = useWorkDisclosure(workId, disclosureEntries);

  /**
   * The chip pressed: the first creates the draft, every later one changes its
   * kind and keeps what has been typed. See `DraftEntry` — a collector who
   * starts a sale and decides it was really an auction should not lose the date.
   *
   * `setEntryKind` and not `setEntryField(…, "kind", …)`, because the authorship
   * claim belongs to the KIND and not to the draft: moving to `attribution` has
   * to fill it from the work and moving away has to clear it, or a stray
   * `claimedArtist` reaches a `.strict()` member that refuses the whole entry.
   * The rule is in the model; this hands it the claim the record supplies.
   *
   * Hoisted out of the handler bundle so the attribution panel's ask can press
   * the same chip rather than growing a second way to start the same draft.
   */
  const chooseKind = (kind: ProvenanceEntryKind): void => {
    setError(null);
    const claim = placed === null ? null : claimedArtistOf(placed.work);

    if (composing === null) {
      setItems((current) => {
        const next = addEntry(current, kind, claim);
        const added = next[next.length - 1];
        if (added && added.kind === "draft") setComposing(added.entry.id);
        return next;
      });
      return;
    }

    setItems((current) => setEntryKind(current, composing, kind, claim));
  };

  return {
    view: resolveWorkRecordView({
      placed,
      workId,
      items,
      openEntryId,
      refusals,
      busy,
      error,
      loadError,
      photographs,
      selectedImageId,
      tab,
      confirmations,
      asks,
      canWriteHistory: input.canWriteHistory ?? true,
      dialogOpen,
      draft,
      agents: workAgents.agents,
      canAssignAgent: input.canAssignAgent,
      representation,
      collections,
      // OND-52. So the holder line names the entity that holds this work
      // instead of saying "Yours" of a company's picture. Nothing is fetched
      // for it, and an id this reader cannot name is not in it.
      entities: entityNames,
      removeArmed,
      correctionField,
      correctionValue,
      agentOpen: workAgents.agentOpen,
      agentId: workAgents.agentId,
      agentNote: workAgents.agentNote,
      agentExpiresOn: workAgents.agentExpiresOn,
      agentMayDelegate: workAgents.agentMayDelegate,
      agentOrganisationId: workAgents.agentOrganisationId,
      agentMayConsign: workAgents.agentMayConsign,
      agentTerms: workAgents.agentTerms,
      agentCheck: workAgents.agentCheck,
    }),
    spotlight,
    closeDocument,
    selectImage,
    reload: () => setReloadKey((key) => key + 1),
    applyLocation: (location) => {
      setPlaced((current) =>
        current === null
          ? current
          : {
              ...current,
              work: {
                ...current.work,
                state: {
                  ...(current.work.state ?? { onLoan: false, loanVenue: null }),
                  locationKind: location.kind,
                  placeText: location.placeText,
                  // OND-105. Taken from the row the server returned, never
                  // carried over from the state being replaced: a work that has
                  // moved inherits no country, and a screen that kept the old
                  // one would show a country the record no longer holds.
                  countryCode: location.countryCode,
                  city: location.city,
                },
              },
            },
      );
    },
    // The error is cleared on the way out, because it renders inside the
    // provenance panel: leaving it set means a refusal vanishes when the reader
    // opens Catalogue Raisonné, and its role="alert" fires again — unattached to
    // anything they did — when they come back minutes later.
    selectTab: (next) => {
      setError(null);
      input.onSelectTab(next);
    },
    // Opens the modal and nothing else. No draft exists until a chip is
    // pressed, so nothing can be filed under a kind nobody chose.
    addProvenanceEntry: () => {
      setError(null);
      setDialogOpen(true);
    },
    /**
     * The attribution panel's ask: the same modal, with the chip already
     * pressed.
     *
     * THE ONE PLACE A CHIP IS PRESELECTED, and the one-way-door argument that
     * forbids it elsewhere does not apply: the reader pressed a control that
     * says what it is going to record, so this is their choice carried through
     * rather than a default they never looked at. The chip row stays live above
     * it, so changing their mind costs one click.
     *
     * Offered only where there is no live claim — `describeAttribution` decides
     * that, and returns a null `ask` otherwise, so this cannot be reached for a
     * work that already has one.
     */
    askAttribution: () => {
      setDialogOpen(true);
      chooseKind("attribution");
    },
    openEntry: (entryId) => {
      setError(null);
      // The unfiltered tab, deliberately: a filtering tab would hide the very
      // row this is about if the claim's kind were not the one it shows. Same
      // rule `?entry=` follows — "routing to the row is what the link is for".
      input.onSelectTab(DEFAULT_WORK_TAB);
      setOpenEntryId(entryId);
      // Who stands behind it is asked for by the effect on `openEntryId`, which
      // is where all three ways of opening a card meet. See it for why this is
      // not a line here.
    },
    assignAgent: workAgents.assignAgent,
    agents: workAgents.handlers,
    // OND-58. Named for the control each one moves, not for the setter they
    // wrap — the frontend skill's rule, and here it is load-bearing: `estate`
    // alone would have invited one handler taking a mode.
    estate: {
      busy: estateBusy,
      failed: estateFailed,
      onSetHidden: (hidden: boolean) => void setEstateHidden(hidden),
      onSetRecordOpen: (open: boolean) => void setEstateRecordShared(open),
    },
    // OND-47 and OND-172. Two handlers on one panel, named for the two acts:
    // choosing how far the work goes, and choosing whether a public view says
    // where it is. Two and not one, because the ticket requires that neither can
    // do the other's work.
    visibility: {
      busy: reachBusy,
      failed: reachFailed,
      onChoose: (visibility: WorkVisibility) => void chooseVisibility(visibility),
      onChooseLocationPublic: (on: boolean) => void chooseLocationPublic(on),
    },
    // OND-22. Named for the acts: move, and the two-press removal. Arming and
    // cancelling are screen state and so belong here, where a test can assert
    // that one press removes nothing.
    // OND-41. Named for the act: choose a field, say what it should say, add
    // the correction. The draft is screen state; the record is read back after.
    correction: {
      busy: correctionBusy,
      failed: correctionFailed,
      onChooseField: (field: WorkCorrectionField) => {
        setCorrectionField(field);
        setCorrectionFailed(null);
      },
      onChangeValue: setCorrectionValue,
      onSubmit: () => void submitCorrection(),
    },
    placement: {
      busy: placementBusy,
      failed: placementFailed,
      onMove: (collectionId: string) => void movePlacement(collectionId),
      onArmRemove: () => {
        setPlacementFailed(null);
        setRemoveArmed(true);
      },
      onCancelRemove: () => setRemoveArmed(false),
      onRemove: () => void removePlacement(),
    },
    // OND-172. The estate's panel, which no owner ever sees: the server answers
    // `attributionControls` null for everybody but the attributed estate, so the
    // view renders nothing rather than a control that would be refused.
    attributionVisibility: {
      busy: attributionBusy,
      failed: attributionFailed,
      onChoose: (on: boolean) => void chooseAttributionPublic(on),
    },
    // OND-79. The whole cluster, as the hook returns it: this one owns a
    // response rather than two booleans, because every write answers with the
    // whole panel and the screen adopts what came back.
    disclosure: {
      panel: workDisclosure.panel,
      draft: workDisclosure.draft,
      entries: disclosureEntries,
      busy: workDisclosure.busy,
      failed: workDisclosure.failed,
      handlers: workDisclosure.handlers,
    },
    history: {
      /**
       * The first chip creates the draft; every later one changes its kind and
       * keeps what has been typed. See `DraftEntry` — a collector who starts a
       * sale and decides it was really an auction should not lose the date.
       */
      onChooseKind: chooseKind,
      onSetField: (key, value) => {
        if (composing === null) return;
        setItems((current) => setEntryField(current, composing, key, value));
      },
      onAddDocuments: (files) => {
        if (composing === null) return;
        setItems((current) => {
          const intake = addDocuments(current, composing, files);
          setRefusals(
            intake.rejected.length > 0
              ? { entryId: composing, sentences: intake.rejected }
              : null,
          );
          return intake.items;
        });
      },
      onSetDocumentKind: (documentId, kind) => {
        if (composing === null) return;
        setItems((current) => setDocumentKind(current, composing, documentId, kind));
      },
      onRemoveDocument: (documentId) => {
        if (composing === null) return;
        setItems((current) => removeDocument(current, composing, documentId));
      },
      onOpenDocument: (documentId) => {
        if (composing !== null) openDocument(composing, documentId);
      },
      onAttachOndoId: (raw) => {
        if (composing === null) return null;
        const attached = attachOndoId(items, composing, raw);
        if (!attached.problem) setItems(attached.items);
        return attached.problem;
      },
      onDetachOndoId: () => {
        if (composing !== null) setItems((current) => detachOndoId(current, composing));
      },
      onAct: (intent) => (composing === null ? null : act(composing, intent)),
      onUnact: (actId) => {
        if (composing !== null) setItems((current) => removeAct(current, composing, actId));
      },
      onSubmit: submitHistory,
      // The only control that discards. Escape and ✕ put the draft down; this
      // throws it away, because that is the only thing Cancel can mean.
      onCancel: () => {
        if (composing !== null) setItems((current) => removeEntry(current, composing));
        setComposing(null);
        setDialogOpen(false);
        setError(null);
      },
      onClose: () => {
        setDialogOpen(false);
      },
    },
    provenance: {
      onToggle: (id) => {
        setOpenEntryId((current) => (current === id ? null : id));
        // And the effect on `openEntryId` asks who stands behind it, for every
        // way a card can come open rather than only this one.
      },
      onSetField: (id, key, value) =>
        setItems((current) => setEntryField(current, id, key, value)),
      onAddDocuments: (id, files) =>
        setItems((current) => {
          const intake = addDocuments(current, id, files);
          setRefusals(
            intake.rejected.length > 0 ? { entryId: id, sentences: intake.rejected } : null,
          );
          return intake.items;
        }),
      onSetDocumentKind: (id, documentId, kind) =>
        setItems((current) => setDocumentKind(current, id, documentId, kind)),
      onRemoveDocument: (id, documentId) =>
        setItems((current) => removeDocument(current, id, documentId)),
      onOpenDocument: openDocument,
      onAttachOndoId: (id, raw) => {
        const attached = attachOndoId(items, id, raw);
        if (!attached.problem) setItems(attached.items);
        return attached.problem;
      },
      onDetachOndoId: (id) => setItems((current) => detachOndoId(current, id)),
      onRemove: (id) => setItems((current) => removeEntry(current, id)),
      onUnact: (id, actId) => setItems((current) => removeAct(current, id, actId)),
      onWithdraw: (id) => {
        void run(() => withdrawProvenanceEntry(id));
      },
      onWithdrawAttestation: withdrawAttestation,
      onWithdrawComment: withdrawComment,
      onAct: act,
      /**
       * Accepting or rejecting ONE paper — OND-62.
       *
       * The refusal is checked here and the write only happens if it passes,
       * which is `onAttachOndoId`'s arrangement: the model owns the rule, the
       * composer keeps the words when it returns a sentence, and nothing is
       * sent that the database has already said it will refuse.
       *
       * `run` then re-reads the chain, which is what makes the entry's standing
       * move: it is derived in the database, so there is nothing to update here
       * and nothing that could be left stale. Sending the same verdict twice is
       * harmless — the server ends the live one in the same statement — so there
       * is no "already judged" state for this handler to keep.
       */
      onJudgeDocument: (_entryId, documentId, stance, reason) => {
        const problem = verdictRefusal(stance, reason);
        if (problem !== null) return problem;

        void run(() =>
          judgeProvenanceDocument(documentId, {
            stance,
            body: stance === "dispute" ? reason.trim() : null,
          }),
        );
        return null;
      },
      onUndoDocumentVerdict: (_entryId, verdictId) => {
        void run(() => withdrawProvenanceDocumentVerdict(verdictId));
      },
    },
  };
}
